import {
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Tooltip,
} from "@mui/material";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useQueryParams } from "hooks/useQueryParams";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { useEffect, useMemo } from "react";

export type TierIIYearPickerProps = {
  selectProps?: SelectProps;
  value?: number | string | null;
  onChange?: (value: number | string | null) => void;
  showNoneOption?: boolean;
  showAllOption?: boolean;
  tooltipText?: string;
  /** If true (default), the query param will be persisted to the URL. */
  persistQueryParam?: boolean;
  /** If true, the selected year will be persisted to localStorage and read from it. Default is false. */
  persistToLocalStorage?: boolean;
};

export function TierIIYearPicker(props: TierIIYearPickerProps) {
  const { persistQueryParam = true, persistToLocalStorage = false } = props;

  const uncontrolledReportingYear = useSelectedReportingYear();
  const {
    filters: { ry },
    setFilters,
  } = useQueryParams<{ ry: string }>();

  const [persistedRy, setPersistedRy] = useLocalStorageState<
    string | undefined
  >("ry", {
    defaultValue: undefined,
  });

  useEffect(() => {
    if (
      persistToLocalStorage &&
      persistedRy &&
      !ry &&
      !props.showNoneOption &&
      persistQueryParam
    ) {
      setFilters((f) => {
        f.ry = persistedRy;
      });
    }
  }, [
    persistToLocalStorage,
    persistedRy,
    ry,
    setFilters,
    props.showNoneOption,
    persistQueryParam,
  ]);

  useEffect(() => {
    if (persistToLocalStorage && ry && ry !== persistedRy) {
      setPersistedRy(ry);
    }
  }, [persistToLocalStorage, persistedRy, ry, setPersistedRy]);

  const numberOfOptions = 10;
  const currentYear = new Date().getFullYear();
  const { sx: selectSx, ...selectProps } = props.selectProps || {};

  const selectValue = useMemo(() => {
    if (props.value !== undefined) {
      return String(props.value ?? "");
    }

    // When no query param exists and localStorage persistence is enabled,
    // use persistedRy directly to avoid flash
    if (persistToLocalStorage && !ry && persistedRy) {
      return String(persistedRy);
    }

    return String(uncontrolledReportingYear);
  }, [
    props.value,
    persistToLocalStorage,
    ry,
    persistedRy,
    uncontrolledReportingYear,
  ]);

  const handleChange = props.onChange
    ? (e: any) => {
        const val = e.target.value === "" ? null : e.target.value;
        props.onChange?.(val);
      }
    : (e: any) => {
        const val = String(e.target.value);
        setFilters((f) => {
          f.ry = val;
        });
      };

  const selectComponent = (
    <FormControl>
      <InputLabel
        id="year-label"
        sx={
          selectProps.variant !== "outlined"
            ? {
                "&.MuiInputLabel-root": {
                  transform: "translate(0, -9px) scale(0.75)",
                },
                overflow: "visible",
              }
            : undefined
        }
      >
        {props.selectProps?.label ?? "Reporting Year"}
      </InputLabel>
      <Select
        variant="standard"
        value={selectValue}
        onChange={handleChange}
        displayEmpty
        labelId="year-label"
        sx={{
          ...(selectProps.variant !== "outlined" && {
            width: "5.375rem",
            "&.MuiInputBase-root": {
              marginTop: "0.375rem",
            },
          }),
          ...selectSx,
        }}
        {...selectProps}
      >
        {props.showNoneOption && <MenuItem value="">None</MenuItem>}
        {Array.from({ length: numberOfOptions }, (_, i) => (
          <MenuItem key={i} value={currentYear - i}>
            {currentYear - i}
            {currentYear - i === currentTierIIReportingYear && (
              <Tooltip
                title="Current reporting year"
                placement="right"
                arrow
                enterDelay={500}
              >
                <span>&nbsp;🔹</span>
              </Tooltip>
            )}
          </MenuItem>
        ))}
        {/* All option with a divider*/}
        {props.showAllOption && <Divider />}
        {props.showAllOption && <MenuItem value="all">All</MenuItem>}
      </Select>
    </FormControl>
  );

  return props.tooltipText ? (
    <Tooltip title={props.tooltipText} placement="right">
      {selectComponent}
    </Tooltip>
  ) : (
    selectComponent
  );
}
